import React, { useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import "./Combobox.css";
import PropTypes from "prop-types";

// Definición de PropTypes para evitar warning
Combobox.propTypes = {
  id: PropTypes.string,
  value: PropTypes.any.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  buttonClass: PropTypes.string,
  disabled: PropTypes.bool,
};

export default function Combobox({
  id,
  value,
  options,
  onChange,
  label = "",
  className = "",
  style = {},
  buttonClass = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // Cierra el menú si se hace click fuera
  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Normalize comparison to avoid type-mismatch (number vs string) issues
  const selected = options.find((opt) => String(opt.value) === String(value));

  // ID para la lista desplegable
  const listId = id ? `${id}-listbox` : "combobox-listbox";

  return (
    <div className={"combobox-root " + className} style={style} ref={ref}>
      <button
        type="button"
        role="combobox"
        aria-expanded={open && !disabled}
        aria-haspopup="listbox"
        aria-label={label}
        aria-controls={listId}
        aria-disabled={disabled}
        className={"combobox-btn " + buttonClass}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <span style={{ pointerEvents: "none" }}>
          {selected ? selected.label : label}
        </span>
        <ChevronDown className="combobox-chevron" size={18} />
      </button>
      {open && !disabled && (
        <ul className="combobox-list" role="listbox">
          {options.map((opt, idx) => (
            <li
              key={String(opt.value ?? idx)}
              role="option"
              aria-disabled={!!opt.disabled}
              aria-selected={String(opt.value) === String(value)}
              className={
                "combobox-option" +
                (String(opt.value) === String(value) ? " combobox-option-active" : "")
                + (opt.disabled ? " combobox-option-disabled" : "")
              }
              tabIndex={0}
              onClick={() => {
                if (opt.disabled) return;
                setOpen(false);
                if (opt.value !== value) onChange(opt.value);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
